
/*!
 * Copyright (C) 2022-2025, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 *		2022-08-17
 *			- Refactor filter-widget.js functionality to <hotel-filter-widget> component.
 *
 *		2022-08-19
 *			- <hotel-filter-widget> now uses <filter-widget>.
 *
 *		2022-08-31
 *			- <hotel-filter-widget> now sends DerbySoft ARI pixel in production.
 *
 *		2022-09-27
 *			- Fix PRIVLINE special offer redirect in <hotel-filter-widget>.
 *
 *		2022-10-14
 *			- Give offerCodes computed property in <hotel-filter-widget> the default value of this.offerCode.
 *
 *    2023-04-03 (jhoward)
 *      - Refactor <hotel-filter-widget> to use TypeScript and type all objects, function returns and arguments.
 *      - Add ReservationDataCache interface in <hotel-filter-widget>.
 *      - Do not map any state in <hotel-filter-widget>, instead use this.$store.state...
 *      - Add <hotel-filter-widget>::selectedFilterCount computed property.
 *      - Watch selected filters in <hotel-filter-widget> and update UX based on what's selected.
 *      - Close filter dialog in <hotel-filter-widget> when click event is fired on document.
 *      - Show "no rooms found" dialog in <hotel-filter-widget> when filters are too specific.
 *
 *    2023-05-24 (jhoward)
 *      - <hotel-filter-widget> now maps checkInDate and checkOutDate from the store so they can be watched.
 *
 *    2023-07-13 (jhoward)
 *      - <hotel-filter-widget> sets both UA and GA4 linkers.
 *`
 *    2023-08-15 (jhoward)
 *      - Do not request hotel rate data if no rooms are found on the page in <hotel-filter-widget>.
 *`
 *    2024-06-10 (jhoward)
 *      - Refactor <hotel-filter-widget> to use new <hotel-calendar> component.
 *`
 *    2024-06-17 (jhoward)
 *      - Fix <hotel-filter-widget> Private Line form input type.
 *`
 *    2024-06-19 (jhoward)
 *      - Fix "CHECK RATES" button in <hotel-filter-widget> when not on /resort page.
 *      - Fix mobile calendar grid in <hotel-filter-widget>.
 *
 *		2024-07-09 (jhoward)
 *			- Fix the number of months being shown at once in <hotel-filter-widget>.
 *
 *		2024-11-18 (jhoward)
 *			- Refactor check in/out date initialization logic to use querystring data first in <hotel-filter-widget>.
 *
 *		2024-12-17 (jhoward)
 *			- Fix redirecting user to booking engine when offer code is supplied in <hotel-filter-widget>.
 *
 *		2025-01-28 (jhoward)
 *			- Fix SSL Warning dialog "CONTINUE TO BOOK" button in <hotel-filter-widget>.
 */

import {ComponentPublicInstance, defineComponent} from "vue";
import WidgetFilters from "../widget-filters.vue";
import HotelCalendar from "./hotelCalendar.vue";
import {
  IntersectionObserverFallback,
  IntersectionObserverFallbackObject
} from "../../helpers/IntersectionObserverFallback";
import {
  FilterCache, HotelFilterWidgetSelections,
  PageListRoomComponent,
  ReservationDataCache,
  RoomAvailabilityData,
  RoomAvailabilityRoomType
} from "./defs";
import {StateFilterSelected} from "../../store/hotel-rooms";
import {mapGetters, mapState} from "vuex";
import LoadingSpinner from "../LoadingSpinner.vue";

interface HotelRoomDomNode extends HTMLDivElement {
  __vue__: PageListRoomComponent;
}

interface SelectedFilter {
  accessibility: string[];
  bed_type: string[];
  price: string[];
  smoking: string[];
  sq_ft: string[];
}

export default defineComponent({
  name: 'hotelFilterWidget',
  components: {
    LoadingSpinner,
    WidgetFilters,
    HotelCalendar
  },
  props: {
    filterType: {
      type: String,
      default: 'SCROLL'
    },
    minCheckInDate: {
      type: String,
      required: true
    },
    minCheckOutDate: {
      type: String,
      required: true
    }
  },
  mounted() {
    $(this.initialize);
  },
  data() {
    return {
      isInitialized: false,

      isRoomDataLoading: false,

      currentScroll: window.scrollY,
      currentWindowHeight: window.innerHeight,
      scrollOffset: 64,

      savedResData: {} as ReservationDataCache,

      filterCacheLabel: 'hotel-filter-widget-selections',
      savedFilterData: {} as FilterCache,

      offerCode: '',
      iata: '',

      calendarComponent: '',
      calendarOpenUpward: false,
      calendarLastScrollRender: -1,
      calendarLastWindowHeightRender: -1,
      calendarMonthNodes: [] as HTMLElement[],
      calendarLoaded: false,
      calendarOpen: false,

      userHasScrolled: false,
      scrollTimer: -1,

      isFilterOpen: false,

      observerTop: null as IntersectionObserver | IntersectionObserverFallbackObject | null,
      observerStickyStuck: false,

      hotelPageLinkGroupNodes: [] as HTMLDivElement[],
      hotelRoomNodes: [] as HotelRoomDomNode[],

      lastRateFetchCheckInDate: null,
      lastRateFetchCheckOutDate: null,

      filterUpdateTimer: -1,
      filterUpdateTimeout: 132
    };
  },
  computed: {
    ...mapGetters(['selectedFilters']),
    ...mapState(['checkInDate', "checkOutDate"]),
    selectedFilterCount(): number {
      return Object.keys(this.$store.getters.selectedFilters).length;
    },
    selectedFilterLabel() {
      let selectedFilterTypesFound = 0;
      let output = 'Choose Filters';
      for (let key in this.$store.getters.selectedFilters) {
        if (this.$store.getters.selectedFilters[key].length) {
          selectedFilterTypesFound++;
        }
      }
      if (1 < selectedFilterTypesFound) {
        output = selectedFilterTypesFound + ' Filter Types Selected';
      } else if (1 === selectedFilterTypesFound) {
        output = selectedFilterTypesFound + ' Filter Type Selected';
      }

      return output;
    },
    containerClasses() {
      return {
        initialized: this.isInitialized,
        'sticky-stuck': this.observerStickyStuck
      };
    },
    calendarWrapperClasses() {
      return {
        hide: !this.calendarOpen,
        'open-upward': this.calendarOpenUpward
      };
    },

    offerCodes() {
      let output = [this.offerCode];
      Array.from(this.hotelRoomNodes).forEach(hotelRoomNode => {
        hotelRoomNode.__vue__.offerCodes.forEach(offerCode => {
          if (-1 === output.indexOf(offerCode)) {
            output.push(offerCode);
          }
        });
      });
      return output;
    },

    checkInDateFormatted(): string {
      if (this.$store.state.checkInDate) {
        return this.reservationWidgetPrepareDate(this.$store.state.checkInDate);
      }

      return '';
    },
    checkOutDateFormatted(): string {
      if (this.$store.state.checkOutDate) {
        return this.reservationWidgetPrepareDate(this.$store.state.checkOutDate);
      }

      return '';
    }
  },
  watch: {
    selectedFilters(): void {
      //- recalculate scroll position for card fade-in [DONE]
      // - set "view all" for all hotel page link groups [DONE]
      // - "scroll" to the closest room type in the same tower being looked at
      //- show "no rooms available" if needed [DONE]
      clearTimeout(this.filterUpdateTimer);
      this.filterUpdateTimer = window.setTimeout(() => {
        this.hotelPageLinkGroupNodes.forEach(pageLinkGroupNode => {
          //make sure all room types are available in this group
          pageLinkGroupNode.classList.add('show-all-items');

          //check if any rooms are available
          if (!pageLinkGroupNode.querySelectorAll('.item:not(.hide)').length) {
            pageLinkGroupNode.classList.add('page-links-all_items_hidden');
          } else {
            pageLinkGroupNode.classList.remove('page-links-all_items_hidden');
          }

          // window.resetPageLinks();
          window.recalcPageLinks(true);

          //scroll to the first room type in the tower being looked at
          const currentScrollPosition = document.querySelector('html')?.scrollTop || 0;
          let newScrollPosition = currentScrollPosition;
          for (let i = 0, l = this.hotelPageLinkGroupNodes.length; i < l; i++) {
            if (this.hotelPageLinkGroupNodes[i].offsetTop > currentScrollPosition) {
              break;
            }
            newScrollPosition = this.hotelPageLinkGroupNodes[i].offsetTop;
            const viewportNode: HTMLElement | null = this.hotelPageLinkGroupNodes[i].querySelector(':scope > .viewport');
            if (viewportNode) {
              newScrollPosition += viewportNode.offsetTop;
            }
          }
          window.scrollTo({
            top: newScrollPosition
          });
        });
      }, this.filterUpdateTimeout);
    },

    savedFilterData(): void {
      //HANDLE CHECK IN/OUT DATES
      this.setCheckInAndCheckOutDates();

      //HANDLE OFFER CODE
      if (this.savedResData.code) {
        this.offerCode = this.savedResData.code;
      }
    },
    offerCode(): void {
      this.saveOfferCodeForLater();
    },

    calendarOpen(): void {
      this.checkCalendarBounds();
    },

    checkInDate(): void {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
      }
    },
    checkOutDate(): void {
      if (this.$store.state.checkInDate && this.$store.state.checkOutDate) {
        window.hotelRoomsVueApp.saveReservationSelections($(this.$el).data('initialized', this.isInitialized), this.$store.state.checkInDate, this.$store.state.checkOutDate);
      }
    }
  },
  methods: {
    initialize() {
      document.addEventListener('click', () => {
        this.calendarOpen = false;
        this.isFilterOpen = false;
      }, {passive: true});
      document.addEventListener('scroll', () => {
        this.currentScroll = window.scrollY;
      }, {
        passive: true
      });

      if (window.hasOwnProperty('pepvalidateInitForm')) {
        window.pepvalidateInitForm.call(this.$refs.form as HTMLFormElement);
      }

      this.getSavedReservationData();

      this.getSavedFilterData();

      this.setCheckInAndCheckOutDates();

      this.scrollOffset = this.$el.clientHeight;

      this.updateObserverOptions();

      this.updateHotelRoomNodes();

      requestAnimationFrame(this.checkCalendarBounds);

      this.getRoomData();

      window.ajaxLinkRegisterCallback(
        'SSL-WARNING',
        () => {
          this.filterSubmitHandler((new CustomEvent('click', {detail: true})));
        }
      );

      this.isInitialized = true;
    },
    setCheckInAndCheckOutDates(): void {
      const startQuerystringDate = window.hotelRoomsVueApp.formatYYYYMMDDToDateObject(window.pepHelperFunctions.getParameterByName('start'));
      const endQuerystringDate = window.hotelRoomsVueApp.formatYYYYMMDDToDateObject(window.pepHelperFunctions.getParameterByName('end'));

      if (startQuerystringDate && endQuerystringDate) {
        window.pepConsole.log('setCheckInAndCheckOutDates', 'querystring', startQuerystringDate, endQuerystringDate);
        this.$store.commit("updateCheckInDate", startQuerystringDate);
        this.$store.commit("updateCheckOutDate", endQuerystringDate);
      } else if (this.savedResData.checkin) {
        window.pepConsole.log('setCheckInAndCheckOutDates', 'savedResData', this.savedResData.checkin, this.savedResData.checkout);
        this.$store.commit("updateCheckInDate", new Date(this.savedResData.checkin));

        if (this.savedResData.checkout) {
          this.$store.commit('updateCheckOutDate', new Date(this.savedResData.checkout));
        } else {
          this.$store.commit('updateCheckOutDate', window.hotelRoomsVueApp.addDays(this.$store.state.checkInDate, 2));
        }
      } else if (this.minCheckInDate.length) {
        window.pepConsole.log('setCheckInAndCheckOutDates', 'minCheckInDate', this.minCheckInDate);
        const newCheckInDate = new Date(this.minCheckInDate);
        if (!!newCheckInDate) {
          this.$store.commit('updateCheckInDate', newCheckInDate);
          this.$store.commit('updateCheckOutDate', window.hotelRoomsVueApp.addDays(this.$store.state.checkInDate, 2));
        }
      }
    },
    getSavedReservationData(): void {
      this.savedResData = window.hotelRoomsVueApp.getReservationSelections();
      if (this.savedResData.code) {
        this.offerCode = this.savedResData.code;
      }
      if (this.savedResData.iata) {
        this.iata = this.savedResData.iata;
      }
    },
    getSavedFilterData() {
      const cachedFilterData: {
        DATA: HotelFilterWidgetSelections
      } | null = window.pepHelperFunctions.getCachedData(this.filterCacheLabel, 1800000) || null;
      if (cachedFilterData?.DATA?.filters) {
        const filterCache = cachedFilterData.DATA.filters;
        for (let key in filterCache) {
          //@ts-ignore
          filterCache[key].forEach(value => {
            this.$store.commit('updateFilter', {
              key,
              value,
              selected: true
            });
          });
        }
      }
    },
    setupUserScrollDetection() {
      //TODO: need to make a bit smarter, if you scroll to rooms, then disable for page load, otherwise make sure to show room
      window.addEventListener('scroll', this.setUserHasScrolled);
      window.addEventListener('touchstart', this.setUserHasScrolled);
    },
    setUserHasScrolled(event: Event | TouchEvent) {
      window.clearTimeout(this.scrollTimer);
      this.userHasScrolled = true;
      event?.target?.removeEventListener('scroll', this.setUserHasScrolled);
      event?.target?.removeEventListener('touchstart', this.setUserHasScrolled);
    },
    getRoomData() {
      //TODO: setup caching with localStorage
      const hotelRoomPageIds = this.getHotelRoomPageIds();
      if (
        hotelRoomPageIds.length
        && this.$store.state.checkInDate
        && this.$store.state.checkOutDate
        && (
          this.$store.state.checkInDate !== this.lastRateFetchCheckInDate
          || this.$store.state.checkOutDate !== this.lastRateFetchCheckOutDate
        )
      ) {
        this.isRoomDataLoading = true;
        this.$store.commit('updateState', ['isRoomAvailabilityLoaded', false]);
        fetch('/library/api/room-rates.php', {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            'Content-type': 'application/json',
            'Accept': 'application/json'
          },
          redirect: 'follow',
          body: JSON.stringify({
            ACTION: 'GET_AVAILABLE_ROOMS_FOR_PAGES_BY_DATES',
            OFFER_CODES: this.offerCodes,
            START_DATE: window.hotelRoomsVueApp.formatDateObjectYYYYMMDD(this.$store.state.checkInDate),
            END_DATE: window.hotelRoomsVueApp.formatDateObjectYYYYMMDD(this.$store.state.checkOutDate),
            PAGE_IDS: hotelRoomPageIds
          })
        })
          .then(response => {
            return response.json();
          })
          .then(json => {
            if (
              'object' === typeof json
              && json.hasOwnProperty('STATUS')
              && json.hasOwnProperty('DATA')
              && json.STATUS === 'SUCCESS'
              && Array.isArray(json.DATA.ROOMS)
            ) {
              this.lastRateFetchCheckInDate = this.$store.state.checkInDate;
              this.lastRateFetchCheckOutDate = this.$store.state.checkOutDate;
              this.$store.commit('updateRooms', json.DATA.ROOMS);
              this.$store.commit('updateAdjacentDates', json.DATA.ADJACENT_DATES || []);
              this.$store.commit('updateState', ['isRoomAvailabilityLoaded', true]);

              this.sendDSPixel();
            }
          })
          .catch(e => {
            window.pepTracking.event('JS Error', 'Get Hotel Room Availability', e.message, 0, true);
          })
          .finally(() => {
            this.isRoomDataLoading = false;
          });
      }
    },
    getHotelRoomPageIds() {
      let output: number[] = [];
      this.hotelRoomNodes.forEach(hotelRoomNode => {
        if (hotelRoomNode.dataset.pageId && -1 === output.indexOf(parseInt(hotelRoomNode.dataset.pageId, 10))) {
          output.push(parseInt(hotelRoomNode.dataset.pageId, 10));
        }
      });
      //add page id for hotel room page if current page is for a hotel room
      if (document.querySelector('#container.hotel-page')) {
        output.push(parseInt(document.querySelector('body')?.dataset?.i || '0', 10));
      }
      return output;
    },


    reservationWidgetPrepareDate(date: Date) {
      const newDay = date.getDate().toString(10);
      const newSuffix = (newDay === '11' || newDay === '12' || newDay === '13')
        ? 'th'
        : (newDay.substr(-1, 1) === '1')
          ? 'st'
          : (newDay.substr(-1, 1) === '2')
            ? 'nd'
            : (newDay.substr(-1, 1) === '3')
              ? 'rd'
              : 'th';

      return window.pepDateFunctions.language.en.daysShort[date.getDay()] + ', ' + window.pepDateFunctions.language.en.monthsShort[date.getMonth()] + ' ' + newDay + newSuffix;
    },


    showCalendars(event?: KeyboardEvent) {
      if (
        (!this.calendarOpen && !event)
        || (!!event && event.key === 'Enter')
      ) {
        event?.preventDefault();
        event?.stopPropagation();
        if ('' === this.calendarComponent) {
          this.calendarComponent = 'hotel-calendar';
        }

        this.calendarOpen = true;
      }
    },
    setDatesFromCalendar(dayCheckIn: Date, dayCheckOut: Date, initialCheckIn: Date, initialCheckOut: Date, selectedDates: string[]): void {
      if (document.querySelector('html')?.classList.contains('ui-mobile')) {
        this.getRoomData();
      }
    },


    filterSubmitHandler(event: Event) {
      window.pepConsole.group('filterSubmitHandler');
      window.pepConsole.log('this.offerCode.length', this.offerCode.length);
      window.pepConsole.log('this.filterType.toUpperCase()', this.filterType.toUpperCase(), 'SCROLL' === this.filterType.toUpperCase());
      // window.pepConsole.log('is not mobile', !document.querySelector('html')?.classList.contains('ui-mobile'));
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (this.offerCode.length) {
        window.pepConsole.log('event.detail', ('detail' in event) ? !!event.detail : false);
        this.filterSubmitHandlerOfferCode(('detail' in event) ? !!event.detail : false);
      } else if ('SCROLL' === this.filterType.toUpperCase()/* && !document.querySelector('html')?.classList.contains('ui-mobile')*/) {
        window.pepConsole.log('run getRoomData()');
        this.getRoomData();
        if (!event.detail || '' + event.detail !== 'skipScroll') {
          this.scrollToRoom(0, true);
        }
      } else {
        window.location.href = '/resort/?STR=1';
      }
      window.pepConsole.groupEnd();
    },
    scrollToRoom(timeout: number, scrollFromAnywhere: boolean) {
      this.setupUserScrollDetection();

      $(window)
        .trigger('resize.page-links', [() => {
          this.scrollTimer = window.setTimeout(() => {
            this.scrollToRoomPageLinkCallback(timeout, scrollFromAnywhere);
          }, timeout);
        }]);
    },
    scrollToRoomPageLinkCallback(timeout: number, scrollFromAnywhere: boolean) {
      let scrollDuration = 250;
      let scrollToContainer = false;

      clearTimeout(this.scrollTimer);

      if (!this.userHasScrolled || (document.querySelector('html') as HTMLElement | null)?.classList?.contains('ui-mobile')) {

        //run after recalculating page link position data
        const visibleHotelRoomNodes = this.hotelRoomNodes.filter(hotelRoomNode => {
          return !hotelRoomNode.classList.contains('hide');
        });

        //scroll if there are visible hotel room nodes, and they aren't all visible
        if (visibleHotelRoomNodes.length && (scrollFromAnywhere || (visibleHotelRoomNodes.length < this.hotelRoomNodes.length))) {

          //scroll to first room that still meets the criteria of the filters if it's below the current viewport
          if (scrollFromAnywhere || (this.currentScroll + this.currentWindowHeight < parseInt(visibleHotelRoomNodes[0].dataset?.offsetTop || '0', 10) - (this.scrollOffset / 2 - (document.querySelector('#header')?.clientHeight || 0) * 2))) {

            const filterWidgetHeight = (document.querySelector('html')?.classList.contains('ui-mobile'))
              ? (this.$refs.mobileCta as HTMLElement | null)?.clientHeight || 0
              : this.$el.clientHeight;
            const containerOffset = (scrollToContainer)
              ? $(visibleHotelRoomNodes[0])?.closest('.body-container')?.offset()?.top || 0
              : $(visibleHotelRoomNodes[0])?.offset()?.top || 0 - 48;
            const topOffset = containerOffset - filterWidgetHeight - (document.querySelector('#header')?.clientHeight || 0);

            if (0 === scrollDuration) {
              window.scrollTo({
                top: topOffset,
                behavior: 'auto'
              });
            } else if ('scrollBehavior' in document.documentElement.style) {
              window.scrollTo({
                top: topOffset,
                behavior: 'smooth'
              });
            } else {
              $('html, body')
                .animate({scrollTop: topOffset}, scrollDuration, 'easeInOutQuad');
            }
          }
        } else if (!visibleHotelRoomNodes.length) {
          window.showOverlayDialog({
            alertMode: true,
            confirmAccept: 'CLOSE',
            confirmTitle: 'No Matches Found',
            confirmBody: 'You can remove filters or try different dates to find available rooms.',
            callback: 'GENERIC-DIALOG-CLOSE'
          });
        }
      }
    },
    filterSubmitHandlerOfferCode(forceClick: boolean = false) {
      window.pepConsole.group('filterSubmitHandlerOfferCode()');
      window.pepConsole.log('window.sslNotify.show', window.sslNotify.show);
      window.pepConsole.log('forceClick', forceClick);
      if (window.sslNotify.show && !forceClick) {
        this.showSslNotification();
      } else {
        this.handleOfferBookingLink();
      }
      window.pepConsole.groupEnd();
    },
    handleOfferBookingLink() {
      const linkParameters = this.getBookingLinkParameters();

      if ('privline' === linkParameters.o.toLowerCase()) {
        window.ajaxLinkRegisterCallback(
          'PRIVLINE-ACCOUNT-CODE',
          function (this: HTMLDivElement, companyCode, offerCode, start, end) {
            //console.log(companyCode, offerCode, start, end);
            $('body')
              .children('.confirm')
              .filter('.dialog')
              .trigger('close');

            if (typeof companyCode === 'string' && $.trim(companyCode).length > 0 && typeof offerCode === 'string' && typeof start === 'string' && typeof end === 'string') {
              const gaLinker = window.pepTracking.getGALinkerParam();
              const ga4Linker = window.pepTracking.getGA4LinkerParam();
              const params: {
                o: string;
                lmsref: string;
                start: string;
                end: string;
                _ga?: string;
                _gl?: string;
              } = {
                o: offerCode,
                lmsref: companyCode,
                start: start,
                end: end
              };

              //add ga linker param
              if (gaLinker.length > 0) params._ga = gaLinker;
              if (ga4Linker.length > 0) params._gl = ga4Linker;
              //redirect to the offer page
              window.location.href = '/offers/?' + $.param(params);
            } else {
              window.showOverlayDialog({
                alertMode: true,
                confirmAccept: 'OK',
                confirmTitle: 'Invalid Company Code',
                confirmBody: 'Please enter a valid company code to continue.'
              }, this);
            }
          }
        );

        //force the user to enter a Reference ID for the PRIVLINE offer
        window.showOverlayDialog({
          confirmAccept: 'OK',
          confirmDeny: 'CANCEL',
          confirmTitle: 'Enter Account Code',
          confirmBody: 'Please enter your company\'s Private Line account code.</p><form class="pepvalidate-v3"><p><label><span class="text">Account Code</span><input type="text" name="lmsref" /><input name="offer_code" type="hidden" value="' + this.offerCode.replace(/[^\da-z]/gi, '') + '" /><input name="start" type="hidden" value="' + linkParameters.start + '" /><input name="end" type="hidden" value="' + linkParameters.end + '" /></form><p>&nbsp;',
          callback: 'PRIVLINE-ACCOUNT-CODE'
        }, this.$el as HTMLElement);
      } else {
        setTimeout(function () {
          window.location.href = '/offers/?' + $.param(linkParameters);
        }, 66);
      }
    },
    getBookingLinkParameters() {
      let parameters: {
        o: string;
        start?: string;
        end?: string;
        _ga?: string;
        _gl?: string;
        iata?: string;
      } = {
        o: this.offerCode
      };
      const gaLinker = window.pepTracking.getGALinkerParam();
      const ga4Linker = window.pepTracking.getGA4LinkerParam();

      if (this.$store.state.checkInDate) {
        parameters.start = this.$store.state.checkInDate.getFullYear() + '-' + (this.$store.state.checkInDate.getMonth() + 1) + '-' + this.$store.state.checkInDate.getDate()
      }
      if (this.$store.state.checkOutDate) {
        parameters.end = this.$store.state.checkOutDate.getFullYear() + '-' + (this.$store.state.checkOutDate.getMonth() + 1) + '-' + this.$store.state.checkOutDate.getDate()
      }

      if (gaLinker.length) {
        parameters._ga = gaLinker;
      }

      if (ga4Linker.length) {
        parameters._gl = ga4Linker;
      }

      if (this.iata.length) {
        parameters.iata = this.iata;
      }

      return parameters;
    },
    reservationWidgetShowErrorMsg(msg: string, title?: string, sslWarning?: boolean): void {
      if (msg.length === 0) {
        return;
      }
      if (typeof title !== 'string') {
        title = '';
      }
      if (typeof sslWarning !== 'boolean') {
        sslWarning = false;
      }

      //close any current dialogs
      document.querySelectorAll('.dialog.confirm.active').forEach(dialogNode => {
        dialogNode.dispatchEvent(new CustomEvent('close'));
      });

      window.showOverlayDialog({
        alertMode: !sslWarning,
        confirmAccept: (sslWarning) ? 'Continue Anyway' : 'OK',
        confirmDeny: (sslWarning) ? 'CANCEL' : '',
        confirmTitle: (title.length) ? title : '',
        confirmBody: msg,
        callback: (sslWarning) ? 'SSL-WARNING' : ''
      }, this.$el as HTMLElement);
    },
    determineBookingPhoneNumber() {
      if (window.Modernizr.touchevents && (document.querySelector('html')?.classList.contains('ui-mobile') || document.querySelector('html')?.classList.contains('ui-tablet'))) {
        return '1-800-821-8568';
      }

      return '1-866-400-0519';
    },
    showSslNotification() {
      this.reservationWidgetShowErrorMsg('Your browser is out-of-date and no longer secure. To protect your information please use a newer browser or call the following phone number to book your reservation:</p><p>' + this.determineBookingPhoneNumber(), 'SSL Warning', true);
      fetch('/library/api/ssl-notify-errors.php', {
        method: 'POST',
        cache: 'no-cache',
        headers: {
          // 'Content-type': 'application/x-www-form-urlencoded',
          'Content-type': 'application/json',
          'Accept': 'application/json'
        },
        redirect: 'follow',
        body: JSON.stringify({
          page: window.location.href
        })
      })
        .catch(e => {
          window.pepTracking.event('JS Error', 'SSL Notify Logging AJAX', e.message, 0, true);
        });
    },


    resortPageButtonClick() {
      if (this.offerCode.length && !this.$el.classList.contains('sticky-stuck')) {
        (this.$refs.form as HTMLFormElement | null)?.dispatchEvent(new Event('submit'));
      } else {
        //if we're already looking at the reservation widget, then scroll to rooms
        if (!this.$el.classList.contains('sticky-stuck')) {
          this.calendarOpen = false;
          this.isFilterOpen = false;
          this.scrollToRoom(0, true);
        } else if ('scrollBehavior' in document.documentElement.style) {
          window.scrollTo({
            top: ($('#container')?.offset()?.top || 0) - ($('#header')?.height() || 0),
            behavior: 'smooth'
          });
        } else {
          $('html, body')
            .animate({scrollTop: ($('#container')?.offset()?.top || 0) - ($('#header')?.height() || 0)}, 350, 'easeInOutQuad');
        }
      }
    },
    nonResortPageButtonClick(event: MouseEvent) {
      if (this.offerCode.length) {
        event.preventDefault();
        event.stopPropagation();
        (this.$refs.form as HTMLFormElement | null)?.dispatchEvent(new Event('submit'));
      }
    },

    offerCodeKeyUpHandler(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const buttonNodes = this.$el.querySelectorAll('.non-resort-page, .filter-submit input');
        for (let i = 0, l = buttonNodes.length; i < l; i++) {
          if ('visible' === getComputedStyle(buttonNodes[i]).visibility) {
            buttonNodes[i].dispatchEvent(new Event('click'));
            break;
          }
        }
      }
    },
    saveOfferCodeForLater() {
      window.window.hotelRoomsVueApp.saveReservationSelections(
        $(this.$el).data('initialized', this.isInitialized),
        this.$store.state.checkInDate,
        this.$store.state.checkOutDate
      );
    },


    updateObserverOptions() {
      //remove old observers if needed
      if (this.observerTop) {
        this.observerTop.disconnect();
      }

      if ('IntersectionObserver' in window) {
        this.updateObserverOptionsIntersectionObserver();
      } else if ('requestAnimationFrame' in window) {
        this.updateObserverOptionsRequestAnimationFrame();
      }

      if (this.observerTop) {
        this.observerTop.observe(this.$refs.stickySentinelTop as HTMLElement);
      }
    },
    updateObserverOptionsIntersectionObserver() {
      this.observerTop = new IntersectionObserver((records) => {
        records.forEach(observerRecord => {
          const targetInfo = observerRecord.boundingClientRect;
          const rootBoundsInfo = observerRecord.rootBounds;

          if (rootBoundsInfo && targetInfo.bottom < rootBoundsInfo.top) {
            this.observerStickyStuck = true;
          }

          if (rootBoundsInfo && targetInfo.bottom >= rootBoundsInfo.top && targetInfo.bottom < rootBoundsInfo.bottom) {
            this.observerStickyStuck = false;
          }
        });
      }, {
        threshold: [0]
      });
    },
    updateObserverOptionsRequestAnimationFrame() {
      this.observerTop = IntersectionObserverFallback((observerFallback: IntersectionObserverFallbackObject) => {
        if (observerFallback.lastScrollCheck !== observerFallback.currentScroll) {
          observerFallback.lastScrollCheck = observerFallback.currentScroll;

          //get all bounding client rectangles
          const boundingRect = observerFallback.elementToObserve?.getBoundingClientRect();
          const rootBoundsInfo = observerFallback.rootBoundsInfo;

          if (boundingRect && boundingRect.bottom <= rootBoundsInfo.top) {
            this.observerStickyStuck = true;
          }

          if (boundingRect && boundingRect.bottom >= rootBoundsInfo.top && boundingRect.bottom <= rootBoundsInfo.bottom) {
            this.observerStickyStuck = false;
          }
        }
      });
    },
    updateHotelRoomNodes(): void {
      this.hotelPageLinkGroupNodes = Array.from(document.querySelectorAll('.body-container.page-links.type-HOTEL'));
      this.hotelRoomNodes = Array.from(document.querySelectorAll('.body-container.page-links.type-HOTEL .item'));
    },


    checkCalendarBounds() {
      if (
        (
          this.calendarLastScrollRender !== this.currentScroll
          || this.calendarLastWindowHeightRender !== this.currentWindowHeight
        )
        && this.calendarOpen
      ) {

        this.calendarLastScrollRender = this.currentScroll;
        this.calendarLastWindowHeightRender = this.currentWindowHeight;

        let openUpward = false;

        if (this.observerStickyStuck) {
          openUpward = false;
        } else {
          const $el = $(this.$el);
          const filterWidgetToTopOfViewport = ($el) ? ($el.offset()?.top || 0) - this.currentScroll : 0;
          const calendarWidgetTopOffset = this.$el.clientHeight;	//calendar widget is docked to the bottom of the filter widget
          const calendarWidgetHeight = this.$el.querySelector('.calendar-wrapper')?.clientHeight || 0;
          const stickyFooterHeight = document.querySelector('#footer .above-the-fold')?.clientHeight || 0;
          const headerHeight = document.querySelector('#header')?.clientHeight || 0;

          //if the calendar widget will display below the fold, let's see if there is space to show above the widget
          if (filterWidgetToTopOfViewport + calendarWidgetTopOffset + calendarWidgetHeight > this.currentWindowHeight - stickyFooterHeight) {

            //calendar will be clipped to some degree if opened down
            if (filterWidgetToTopOfViewport - calendarWidgetHeight > headerHeight) {

              //looks like there is space to open the calendar widget upward
              openUpward = true;
            }
          }
        }

        this.calendarOpenUpward = openUpward;
      }

      requestAnimationFrame(this.checkCalendarBounds);
    },

    sendDSPixel: function () {
      const stayLength = window.hotelRoomsVueApp.daysBetween(this.$store.state.checkInDate, this.$store.state.checkOutDate);
      const allRoomTypes: RoomAvailabilityRoomType[] = this.$store.state.ROOMS.map((room: RoomAvailabilityData) => {
        return (Array.isArray(room.ROOM_TYPES))
          ? room.ROOM_TYPES.filter(roomType => !roomType.hasOwnProperty('MIN_STAY_DAYS') || roomType.MIN_STAY_DAYS <= stayLength)
          : [];
      })
        .reduce((acc: RoomAvailabilityRoomType[], carry: RoomAvailabilityRoomType[]) => acc.concat(carry), []);
      const cheapestRoomType = this.getCheapestRoomType(allRoomTypes);
      if (
        cheapestRoomType
        && cheapestRoomType.hasOwnProperty('ROOM_TOTAL_PRICE')
        && cheapestRoomType.hasOwnProperty('ROOM_CODE_WITH_WING')
        && cheapestRoomType.hasOwnProperty('ROOM_TITLE')
      ) {
        const priceSubtotal = cheapestRoomType.ROOM_TOTAL_PRICE;
        let taxAmount = priceSubtotal * 0.13;
        let feeAmount = 2 * stayLength;
        if (cheapestRoomType.hasOwnProperty('RESORT_FEE_RATE')) {
          const resortFeeAmount = cheapestRoomType.RESORT_FEE_RATE * stayLength;
          feeAmount += resortFeeAmount;
          taxAmount += resortFeeAmount * 0.13;
        }

        this.createDSPixel({
          check_in_date: window.hotelRoomsVueApp.formatDateObjectYYYYMMDD(this.$store.state.checkInDate),
          check_out_date: window.hotelRoomsVueApp.formatDateObjectYYYYMMDD(this.$store.state.checkOutDate),
          stay_length: stayLength,

          total_guests: 2,
          adults: 2,
          children: 0,

          price_base: priceSubtotal,
          price_tax_fees: taxAmount + feeAmount,
          price_total: priceSubtotal + taxAmount + feeAmount,
          price_currency: 'USD',

          room_type_id: (cheapestRoomType.hasOwnProperty('ROOM_CODE_WITH_WING')) ? cheapestRoomType.ROOM_CODE_WITH_WING : '',
          room_type: cheapestRoomType.ROOM_TITLE,

          rate_plan_id: 'INTERNT',
          rate_plan_name: 'INTERNT',

          event_type: 'rate_selection',
        });
      }
    },
    getCheapestRoomType(roomTypes: RoomAvailabilityRoomType[]): RoomAvailabilityRoomType | null {
      if (roomTypes.length) {
        return roomTypes.reduce((carryRoomType, currentRoomType) => {
          if (
            carryRoomType.hasOwnProperty('ROOM_TOTAL_PRICE')
            && currentRoomType.hasOwnProperty('ROOM_TOTAL_PRICE')
            && carryRoomType.ROOM_TOTAL_PRICE > currentRoomType.ROOM_TOTAL_PRICE
          ) {
            return currentRoomType;
          }

          return carryRoomType;
        });
      }

      return null;
    },
    createDSPixel(data: { [key: string]: string | number | null }) {
      window._bparams = $.extend({
        pixel_id: '10126',
        account_id: 'peppermill',
        is_landing_page: 'FALSE',
        hotel_id: '54908',
        rooms: 1
      }, data);

      //only send data on production site
      if ('dev.' !== location.host.substring(0, 4)) {
        const scriptNode = document.createElement('script');
        scriptNode.type = 'text/javascript';
        scriptNode.async = true;
        scriptNode.src = 'https://linkcenterus.derbysoftsec.com/pixel/v1/pixel.js?v=' + Math.round(new Date().getTime() / 1000);
        const s = document.querySelector('script');
        if (s && s.parentNode) {
          s.parentNode.insertBefore(scriptNode, s);
        }
      }
    },

    updateFilter(payload: StateFilterSelected) {
      this.$store.commit('updateFilter', {
        key: payload.key || '',
        value: payload.value || '',
        selected: payload.selected || false
      });
    },
    handleCalendarClose(): void {
      this.calendarOpen = false;
      (this.$refs.check_in_input as HTMLElement | null)?.focus();
    }
  }
})
